import type { SessionWorkspace } from "@/lib/api/sessionPortal";

export type DestinationIntent = "inherit" | "manual" | "google_meet" | "zoom";

export function authorizedDestinationIntents(
  workspace: SessionWorkspace,
): DestinationIntent[] {
  if (!workspace.course.term_open) return [];
  const allowed = workspace.viewer.allowed_actions;
  const intents: DestinationIntent[] = [];
  if (allowed.includes("clear_session_override")) intents.push("inherit");
  // `enabled_providers` is carried only by the teaching projection. Its
  // presence is the server-shaped manual writer authority; an empty list still
  // permits an in-person exact-class destination.
  if (
    workspace.destination.enabled_providers !== undefined
    || allowed.includes("set_meeting_link")
    || allowed.includes("fix_meeting_link")
  ) {
    intents.push("manual");
  }
  if (allowed.includes("replace_google_meet") || allowed.includes("create_google_meet")) {
    intents.push("google_meet");
  }
  if (allowed.includes("replace_zoom_meeting") || allowed.includes("create_zoom_meeting")) {
    intents.push("zoom");
  }
  return intents;
}

export function hasAuthorizedDestinationEditorChoice(
  workspace: SessionWorkspace,
): boolean {
  return authorizedDestinationIntents(workspace).length > 0;
}

export function initialDestinationIntent(
  workspace: SessionWorkspace,
  authorized: DestinationIntent[],
): DestinationIntent {
  let selected: DestinationIntent | null;
  switch (workspace.destination.resolution_action) {
    case "clear_session_override":
      selected = "inherit";
      break;
    case "create_google_meet":
    case "replace_google_meet":
      selected = "google_meet";
      break;
    case "create_zoom_meeting":
    case "replace_zoom_meeting":
      selected = "zoom";
      break;
    case "set_meeting_link":
    case "fix_meeting_link":
      selected = "manual";
      break;
    default:
      selected = workspace.destination.effective_source === "session_override"
        ? "manual"
        : "inherit";
  }
  if (authorized.includes(selected)) return selected;
  // The server action can change while this mounted editor is open. Falling
  // back to the first rendered choice prevents a RadioGroup value that has no
  // corresponding control.
  return authorized[0] ?? "manual";
}
